import React from "react";
import { Volume2 } from "lucide-react";
import { speak, chime } from "@/lib/speech";

/* Reusable UI atoms for LittleLearner */

export function Sparkles() {
  const dots = Array.from({ length: 12 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((_, i) => (
        <span
          key={i}
          className="absolute text-yellow-300 ll-float-slow"
          style={{
            left: `${(i * 83) % 100}%`,
            top: `${(i * 47) % 100}%`,
            animationDelay: `${i * 0.4}s`,
            opacity: 0.7,
            fontSize: `${12 + (i % 4) * 4}px`,
          }}
        >
          ✦
        </span>
      ))}
    </div>
  );
}

export function Speaker({ text, size = 20, testId }) {
  return (
    <button
      data-testid={testId || "speaker-button"}
      onClick={(e) => { e.stopPropagation(); speak(text); }}
      className="ll-speaker"
      aria-label={`Hear ${text}`}
      title="Hear it"
    >
      <Volume2 size={size} />
    </button>
  );
}

export function StarBadge({ count, testId }) {
  return (
    <div data-testid={testId || "star-badge"} className="inline-flex items-center gap-1.5 bg-yellow-100 text-yellow-800 font-bold px-3 py-1.5 rounded-full border-2 border-yellow-200 shadow-sm">
      <span className="text-lg">⭐</span>
      <span className="font-display">{count}</span>
    </div>
  );
}

export function ProgressBar({ percent, testId }) {
  return (
    <div className="ll-progress" data-testid={testId || "progress-bar"} role="progressbar" aria-valuenow={percent}>
      <span style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}

export function Confetti({ show }) {
  if (!show) return null;
  const bits = Array.from({ length: 30 });
  const colors = ["#FDE047", "#A78BFA", "#F472B6", "#7DD3FC", "#6EE7B7", "#FB923C"];
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {bits.map((_, i) => (
        <span key={i}
          style={{
            position: "absolute",
            top: "-30px",
            left: `${Math.random() * 100}%`,
            width: 10, height: 14,
            background: colors[i % colors.length],
            borderRadius: 3,
            animation: `ll-confetti-fall ${1.6 + Math.random() * 1.4}s linear ${Math.random() * 0.6}s forwards`,
          }}
        />
      ))}
    </div>
  );
}

// Feedback overlay after selection
export function Feedback({ state, onNext, onRetry, correctLabel = "Great job! ⭐", tryLabel = "Almost! Try again 💜" }) {
  if (!state) return null;
  const correct = state === "correct";
  return (
    <div className="fixed inset-x-0 bottom-24 md:bottom-8 flex justify-center z-40 px-4">
      <div className={`ll-card ll-pop px-6 py-4 flex items-center gap-4 ${correct ? "bg-emerald-50 border-emerald-200" : "bg-pink-50 border-pink-200"}`}>
        <div className={`text-4xl ${correct ? "" : "ll-shake"}`}>{correct ? "🎉" : "🤔"}</div>
        <div>
          <div className="font-display font-bold text-lg text-purple-900">{correct ? correctLabel : tryLabel}</div>
          <div className="flex gap-2 mt-2">
            {correct ? (
              <button data-testid="next-question-button" onClick={onNext} className="ll-btn ll-btn-primary py-2 px-5 text-sm">Next →</button>
            ) : (
              <button data-testid="retry-button" onClick={onRetry} className="ll-btn ll-btn-secondary py-2 px-5 text-sm">Try again</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function playCorrect(word) { chime("correct"); if (word) setTimeout(() => speak(word), 240); }
export function playWrong() { chime("wrong"); }
